import { registerSW } from 'virtual:pwa-register'
import { ref, shallowRef } from 'vue'
import type { Router } from 'vue-router'
import { syncPush } from '@/features/settings'
import { useSessionStore } from '@/shared/session'

/**
 * PWA (ТЗ 18.7, задача 4.2): регистрация service worker, обновление по кнопке, установка
 * по `beforeinstallprompt` и сообщения service worker'а странице (переход по нажатию на
 * уведомление, переоформление push-подписки).
 */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

/** Готова новая версия: показываем «Обновить», сама страница не перезагружается. */
export const needRefresh = ref(false)
/** Браузер предлагает установку (Android, десктопный Chrome). */
export const installPrompt = shallowRef<BeforeInstallPromptEvent | null>(null)

let updateSW: ((reloadPage?: boolean) => Promise<void>) | null = null

/** Проверять новую версию раз в час: вкладка на телефоне живёт днями. */
const UPDATE_CHECK_MS = 60 * 60 * 1000

export function setupPwa(router: Router): void {
  // Связь вернулась после запуска без сети: проверяем вход и заново проходим guard.
  window.addEventListener('online', () => {
    const session = useSessionStore()
    if (!session.isUnverified) return
    void session.refresh().then(() => {
      const current = router.currentRoute.value
      return router.replace({ path: current.fullPath, force: true })
    })
  })
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault()
    installPrompt.value = event as BeforeInstallPromptEvent
  })
  window.addEventListener('appinstalled', () => {
    installPrompt.value = null
  })
  if (!('serviceWorker' in navigator)) return
  updateSW = registerSW({
    immediate: true,
    onNeedRefresh: () => {
      needRefresh.value = true
    },
    onRegisteredSW: (_url, registration) => {
      if (registration)
        setInterval(() => void registration.update().catch(() => undefined), UPDATE_CHECK_MS)
    },
  })
  navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
    const data = event.data as { type?: string; url?: string } | null
    if (data?.type === 'navigate' && typeof data.url === 'string' && data.url.startsWith('/')) {
      void router.push(data.url)
    } else if (data?.type === 'push-resubscribe' && useSessionStore().accessToken) {
      void syncPush().catch(() => undefined)
    }
  })
}

/** Включить новую версию: service worker сменяется, страница перезагружается. */
export async function applyUpdate(): Promise<void> {
  needRefresh.value = false
  await updateSW?.(true)
}

/** Системный диалог установки. `true`, если пользователь согласился. */
export async function install(): Promise<boolean> {
  const event = installPrompt.value
  if (!event) return false
  await event.prompt()
  const { outcome } = await event.userChoice
  installPrompt.value = null
  return outcome === 'accepted'
}
